import * as React from 'react';
import Button from '@mui/material/Button';
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import {createOrUpdateTemplate, deleteTemplate, getTemplate, uploadFile, DestinationFolder} from '../Api';
import {useNavigate, useParams} from 'react-router-dom';
import TemplateDto from '../models/dtos/TemplateDto';
import LocalizedOfferDto from '../models/dtos/LocalizedOfferDto';
import LocalizedTemplateDto from '../models/dtos/LocalizedTemplateDto';

const SUPPORTED_LANGUAGES = ['en', 'ru', 'ro', 'pt', 'es', 'de', 'it', 'fr', 'pl', 'tr', 'hi'];

export default function AddFunnelLocalization() {
    let editedTemplateId = useParams()['id'] as string;
    let isEditMode = editedTemplateId != undefined;

    const [state, setState] = React.useState({
        templatePresent: false,
        template: {
            id: '',
            localizations: {} as LocalizedTemplateDto,
            customBetcoins: null
        } as TemplateDto,
        dialogOpen: false,
        dialogMsg: '',
        previewLanguage: 'en',
        uploading: {} as { [lang: string]: boolean },
        addLangDialogOpen: false
    });
    const navigate = useNavigate();

    const activeLanguages = ['en', ...SUPPORTED_LANGUAGES.filter(l => l !== 'en' && state.template.localizations[l] != null)];
    const availableLanguages = SUPPORTED_LANGUAGES.filter(l => l !== 'en' && state.template.localizations[l] == null);

    const addLanguage = (lang: string) => {
        setState(prevState => ({
            ...prevState,
            template: {
                ...prevState.template,
                localizations: {
                    ...prevState.template.localizations,
                    [lang]: {title: '', subtitle: '', iconUrl: ''}
                }
            },
            addLangDialogOpen: false
        }));
    };

    const removeLanguage = (lang: string) => {
        setState(prevState => {
            const newLocalizations = {...prevState.template.localizations};
            delete newLocalizations[lang];
            return {
                ...prevState,
                template: {...prevState.template, localizations: newLocalizations}
            };
        });
    };

    React.useEffect(() => {
        const fetchData = async () => {
            if (isEditMode) {
                let template = await getTemplate(editedTemplateId);
                if (template != null) {
                    setState(prevState => ({
                        ...prevState,
                        template: template,
                        templatePresent: true
                    }));
                }
            }
        };
        fetchData();
    }, []);

    const getLocalization = (lang: string): LocalizedOfferDto => {
        return state.template.localizations[lang] || {title: '', subtitle: '', iconUrl: ''};
    };

    const updateLocalization = (lang: string, field: keyof LocalizedOfferDto, value: string) => {
        setState(prevState => ({
            ...prevState,
            template: {
                ...prevState.template,
                localizations: {
                    ...prevState.template.localizations,
                    [lang]: {
                        ...(prevState.template.localizations[lang] || {title: '', subtitle: '', iconUrl: ''}),
                        [field]: value
                    }
                }
            }
        }));
    };

    const handleFileUpload = async (lang: string, file: File) => {
        setState(prevState => ({...prevState, uploading: {...prevState.uploading, [lang]: true}}));
        try {
            const path = await uploadFile(file, DestinationFolder.Ads);
            if (path) {
                updateLocalization(lang, 'iconUrl', path);
            } else {
                setState(prevState => ({
                    ...prevState,
                    dialogMsg: `Failed to upload file for language: ${lang}`,
                    dialogOpen: true
                }));
            }
        } finally {
            setState(prevState => ({...prevState, uploading: {...prevState.uploading, [lang]: false}}));
        }
    };

    const onCreateClicked = async () => {
        let hasError = false;
        let errorMsg = '';

        if (state.template.id == undefined || state.template.id.length < 3 || state.template.id.length > 30 || !new RegExp('^[_0-9A-Za-z]+$').test(state.template.id)) {
            hasError = true;
            errorMsg = "Localization name should be between 3 and 30 characters and contain only latin characters";
        } else if (!state.template.localizations['en'] || !state.template.localizations['en'].title || !state.template.localizations['en'].iconUrl) {
            hasError = true;
            errorMsg = "English (en) localization must have at least a title and icon URL as it is the fallback language";
        }

        if (!hasError) {
            let result = await createOrUpdateTemplate(state.template);
            hasError = !result;
            if (!result) {
                errorMsg = "Server error!";
            }
        }

        if (hasError) {
            setState(prev => ({...prev, dialogMsg: errorMsg, dialogOpen: true}));
        } else {
            navigate('/funnels/localizations');
        }
    };

    const onDeleteClicked = async () => {
        let hasError = false;
        let errorMsg = '';

        let result = await deleteTemplate(state.template.id);
        hasError = !result;
        if (!result) {
            errorMsg = "Server error!";
        }

        if (hasError) {
            setState(prev => ({...prev, dialogMsg: errorMsg, dialogOpen: true}));
        } else {
            navigate('/funnels/localizations');
        }
    };

    const handleDialogClose = () => {
        setState(prev => ({...prev, dialogOpen: false}));
    };

    const previewLoc = getLocalization(state.previewLanguage);
    const fallbackLoc = getLocalization('en');

    return (<div>
        <Stack gap="50px 50px" sx={{mx: 5, my: 5}} justifyContent='center' alignItems='center'>
            <Grid container spacing={2} columns={{xs: 1, sm: 2, md: 4, lg: 6}} alignItems='center'>
                <Grid item xs={0} sm={0} md={1} lg={2}/>
                <Grid item xs={2}>
                    <Typography variant="h3">
                        {isEditMode ? "Edit localization template" : "Add new localization template"}
                    </Typography>
                </Grid>
                <Box width="100%"/>
                <Grid item xs={0} sm={0} md={1} lg={2}/>
                <Grid item xs={2}>
                    <Typography variant="h6">Localization template:</Typography>
                </Grid>
                <Box width="100%"/>
                {!state.templatePresent && isEditMode ? <React.Fragment>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="progress">
                        <CircularProgress size={64} style={{marginBottom: '32px'}}/>
                    </Grid>
                </React.Fragment> : <React.Fragment>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="localizationId">
                        <TextField label="Localization id (only letters)" variant="filled"
                                   value={state.template.id || ''} fullWidth={true}
                                   disabled={isEditMode}
                                   onChange={e => {
                                       setState(prevState => ({
                                           ...prevState,
                                           template: {...prevState.template, id: e.target.value}
                                       }));
                                   }}/>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2}>
                        <Typography variant="h6">Localizations per language:</Typography>
                        <Typography variant="body2" color="text.secondary">
                            English (en) is the required fallback language. Other languages are optional.
                        </Typography>
                    </Grid>

                    {activeLanguages.map(lang => (
                        <React.Fragment key={lang}>
                            <Box width="100%"/>
                            <Grid item xs={0} sm={0} md={1} lg={2}/>
                            <Grid item xs={2}>
                                <Divider sx={{my: 1}}/>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>
                                        {lang.toUpperCase()} {lang === 'en' ? '(required - fallback)' : ''}
                                    </Typography>
                                    {lang !== 'en' && (
                                        <Button size="small" color="error" variant="text"
                                                onClick={() => removeLanguage(lang)}>
                                            Remove
                                        </Button>
                                    )}
                                </Box>
                                <TextField
                                    label={`Title (${lang})`}
                                    variant="filled"
                                    value={getLocalization(lang).title || ''}
                                    fullWidth={true}
                                    sx={{mb: 2}}
                                    onChange={e => updateLocalization(lang, 'title', e.target.value)}
                                />
                                <TextField
                                    label={`Subtitle (${lang})`}
                                    variant="filled"
                                    value={getLocalization(lang).subtitle || ''}
                                    fullWidth={true}
                                    sx={{mb: 2}}
                                    onChange={e => updateLocalization(lang, 'subtitle', e.target.value)}
                                />
                                <Box display="flex" alignItems="flex-start" gap={3}>
                                    <Box flex={1}>
                                        <TextField
                                            label={`Icon URL (${lang})`}
                                            variant="filled"
                                            value={getLocalization(lang).iconUrl || ''}
                                            fullWidth={true}
                                            sx={{mb: 1}}
                                            onChange={e => updateLocalization(lang, 'iconUrl', e.target.value)}
                                        />
                                        <Button variant="outlined" component="label" size="small"
                                                disabled={state.uploading[lang]}>
                                            {state.uploading[lang] ? 'Uploading...' : `Upload icon (${lang})`}
                                            <input type="file" hidden accept="image/*"
                                                   onChange={e => {
                                                       if (e.target.files && e.target.files[0]) {
                                                           handleFileUpload(lang, e.target.files[0]);
                                                       }
                                                   }}/>
                                        </Button>
                                    </Box>
                                    {getLocalization(lang).iconUrl && (
                                        <Box sx={{flexShrink: 0}}>
                                            <img src={getLocalization(lang).iconUrl}
                                                 width={200} height={160}
                                                 alt={`icon-${lang}`}
                                                 style={{objectFit: 'contain', border: '1px solid #eee', borderRadius: 6, display: 'block'}}/>
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                        </React.Fragment>
                    ))}

                    {availableLanguages.length > 0 && (<>
                        <Box width="100%"/>
                        <Grid item xs={0} sm={0} md={1} lg={2}/>
                        <Grid item xs={2}>
                            <Button variant="outlined" fullWidth={true} style={{height: '54px'}}
                                    onClick={() => setState(prev => ({...prev, addLangDialogOpen: true}))}>
                                Add other localization
                            </Button>
                        </Grid>
                    </>)}

                    <Box width='100%'/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2}>
                        <Typography variant="h6">Preview:</Typography>
                    </Grid>
                    <Box width='100%'/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={1} key="languageSelect">
                        <FormControl fullWidth>
                            <InputLabel id="preview-language-label">Language for preview:</InputLabel>
                            <Select
                                labelId="preview-language-label"
                                id="preview-language-select"
                                value={state.previewLanguage}
                                onChange={e => setState(prev => ({...prev, previewLanguage: e.target.value}))}
                                label="Language for preview:">
                                {SUPPORTED_LANGUAGES.map(lang => (
                                    <MenuItem value={lang} key={lang}>{lang.toUpperCase()}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="localizationPreview">
                        <Card sx={{width: '100%'}}>
                            <CardContent>
                                {(previewLoc.iconUrl || fallbackLoc.iconUrl) && (
                                    <img
                                        src={previewLoc.iconUrl || fallbackLoc.iconUrl}
                                        width={160} height={128}
                                        alt="preview"
                                        style={{objectFit: 'contain'}}/>
                                )}
                                <Typography gutterBottom variant="h6" component="div">
                                    Title: {previewLoc.title || fallbackLoc.title || ''}
                                </Typography>
                                <Typography gutterBottom variant="body1" component="div">
                                    Subtitle: {previewLoc.subtitle || fallbackLoc.subtitle || ''}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Box width='100%'/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={isEditMode ? 1 : 2} key="addMatchContainer">
                        <Button variant="contained" fullWidth={true} style={{height: '54px'}}
                                onClick={() => onCreateClicked()}>
                            {isEditMode ? "Update localization" : "Add localization"}
                        </Button>
                    </Grid>
                    {isEditMode ? <Grid item xs={1} key="deleteMatchContainer">
                        <Button variant="outlined" fullWidth={true} style={{height: '54px'}}
                                onClick={() => onDeleteClicked()}>Delete localization</Button>
                    </Grid> : <React.Fragment/>}
                    <Box width='100%'/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="goToBannersContainer">
                        <Button variant="outlined" fullWidth={true} style={{height: '54px'}}
                                onClick={() => navigate('/funnels/localizations')}>
                            Show all funnel localizations
                        </Button>
                    </Grid>
                </React.Fragment>}
            </Grid>
        </Stack>
        <Dialog
            open={state.dialogOpen}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title">Error</DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    {state.dialogMsg}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleDialogClose}>Got it</Button>
            </DialogActions>
        </Dialog>
        <Dialog
            open={state.addLangDialogOpen}
            onClose={() => setState(prev => ({...prev, addLangDialogOpen: false}))}>
            <DialogTitle>Select language to add</DialogTitle>
            <DialogContent>
                <Stack spacing={1} sx={{mt: 1, minWidth: 250}}>
                    {availableLanguages.map(lang => (
                        <Button key={lang} variant="outlined" fullWidth
                                onClick={() => addLanguage(lang)}>
                            {lang.toUpperCase()}
                        </Button>
                    ))}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setState(prev => ({...prev, addLangDialogOpen: false}))}>Cancel</Button>
            </DialogActions>
        </Dialog>
    </div>);
}